import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Edit, Trash2, Search, FileText, Send,
  Printer, Download, Eye, DollarSign
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoicesPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const printRef = useRef(null);

  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Form
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    client_id: "", client_name: "", client_email: "",
    items: [{ description: "", qty: 1, price: 0, total: 0 }],
    payment_method: "", notes: "", status: "draft"
  });

  // Preview
  const [previewInvoice, setPreviewInvoice] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const fetchAll = async () => {
    try {
      const [invRes, cliRes, prodRes] = await Promise.all([
        axios.get(`${API}/admin/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setInvoices(invRes.data.invoices);
      setClients(cliRes.data.clients);
      setProducts(prodRes.data.products.filter(p => p.is_active !== false));
    } catch (e) {
      toast.error("Error al cargar datos");
    } finally { setIsLoading(false); }
  };

  const handleClientSelect = (clientId) => {
    const c = clients.find(cl => cl.id === clientId);
    if (c) setForm(p => ({ ...p, client_id: clientId, client_name: c.name, client_email: c.email }));
  };

  const updateItem = (idx, field, value) => {
    setForm(p => {
      const items = [...p.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === "qty" || field === "price") {
        items[idx].total = (parseFloat(items[idx].qty) || 0) * (parseFloat(items[idx].price) || 0);
      }
      return { ...p, items };
    });
  };

  // Stable client-side row IDs so React `key` survives add/remove without stale closures
  const newRow = () => ({ _uid: `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, description: "", qty: 1, price: 0, total: 0 });
  const ensureUids = (items) => (items || []).map((it) => (it._uid ? it : { ...it, _uid: `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }));

  const addItem = () => setForm(p => ({ ...p, items: [...p.items, newRow()] }));
  const removeItem = (idx) => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const calcSubtotal = () => form.items.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);

  const handleOpenCreate = () => {
    setEditingInvoice(null);
    setForm({ client_id: "", client_name: "", client_email: "", items: [newRow()], payment_method: "", notes: "", status: "draft" });
    setIsFormOpen(true);
  };

  const handleOpenEdit = (inv) => {
    setEditingInvoice(inv);
    setForm({
      client_id: inv.client_id || "", client_name: inv.client_name || "", client_email: inv.client_email || "",
      items: inv.items?.length ? ensureUids(inv.items) : [newRow()],
      payment_method: inv.payment_method || "", notes: inv.notes || "", status: inv.status || "draft"
    });
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.client_name) { toast.error("Selecciona un cliente"); return; }
    if (!form.items.some(i => i.description)) { toast.error("Agrega al menos un item"); return; }
    setIsSaving(true);
    const subtotal = calcSubtotal();
    const payload = { ...form, subtotal, total: subtotal };
    try {
      if (editingInvoice) {
        await axios.put(`${API}/admin/invoices/${editingInvoice.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Factura actualizada");
      } else {
        await axios.post(`${API}/admin/invoices`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Factura creada");
      }
      setIsFormOpen(false);
      fetchAll();
    } catch (e) { toast.error("Error al guardar"); }
    finally { setIsSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta factura?")) return;
    try {
      await axios.delete(`${API}/admin/invoices/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Factura eliminada");
      fetchAll();
    } catch (e) { toast.error("Error al eliminar"); }
  };

  const handleSendEmail = async (inv) => {
    try {
      await axios.post(`${API}/admin/invoices/${inv.id}/send-email`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Factura enviada a ${inv.client_email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
  };

  // Escape user-controlled values to prevent XSS in print/PDF HTML
  const esc = (v) =>
    String(v ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const buildInvoiceHTML = (inv) => {
    const num = String(inv.invoice_number).padStart(4, '0');
    const date = inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '';
    let itemsHTML = '';
    (inv.items || []).forEach(item => {
      const p = parseFloat(item.price) || 0;
      const t = parseFloat(item.total) || 0;
      itemsHTML += `<tr><td style="padding:10px 12px;border-bottom:1px solid #eee">${esc(item.description)}</td><td style="padding:10px 12px;text-align:center;border-bottom:1px solid #eee">${esc(item.qty || 1)}</td><td style="padding:10px 12px;text-align:right;border-bottom:1px solid #eee">$${p.toFixed(2)}</td><td style="padding:10px 12px;text-align:right;border-bottom:1px solid #eee">$${t.toFixed(2)}</td></tr>`;
    });
    const subtotal = parseFloat(inv.subtotal) || 0;
    const total = parseFloat(inv.total) || 0;

    return `<!DOCTYPE html><html><head><title>Factura #${esc(num)}</title>
<style>
  @page { size: letter; margin: 0.75in; }
  body { font-family: Arial, sans-serif; margin: 0; padding: 40px; color: #333; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #c45c2c; color: white; }
  th, td { padding: 10px 12px; text-align: left; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #c45c2c; padding-bottom: 15px; margin-bottom: 20px; }
  .total-line { text-align: right; margin-top: 15px; }
  .total-amount { font-size: 22px; color: #c45c2c; font-weight: bold; }
  .footer { display: flex; justify-content: space-between; border-top: 1px solid #eee; padding-top: 15px; margin-top: 20px; }
  @media print { body { padding: 20px; } }
</style></head><body>
  <div class="header">
    <div style="text-align:center">
      <img src="${window.location.origin}/assets/logo-sally-light.jpg" alt="SALLY Better" style="height:80px;width:auto;border-radius:6px;display:block;margin:0 auto" />
      <a href="https://www.sallybetter.com" style="display:block;margin-top:6px;color:#c45c2c;font-size:12px;text-decoration:none;text-align:center">www.sallybetter.com</a>
    </div>
    <div style="text-align:right">
      <p style="margin:0;font-weight:bold;font-size:14px">Invoice #${esc(num)}</p>
      <p style="margin:3px 0 0;color:#666;font-size:13px">Date: ${esc(date)}</p>
    </div>
  </div>
  <div style="margin-bottom:20px">
    <p style="margin:0;color:#999;font-size:11px;text-transform:uppercase">Invoice for:</p>
    <p style="margin:3px 0 0;font-weight:bold;font-size:16px">${esc(inv.client_name)}</p>
    ${inv.client_email ? `<p style="margin:2px 0 0;color:#666;font-size:13px">${esc(inv.client_email)}</p>` : ''}
  </div>
  <table><thead><tr><th style="text-align:left">Description</th><th style="text-align:center;width:70px">Qty.</th><th style="text-align:right;width:100px">Price</th><th style="text-align:right;width:100px">Total</th></tr></thead><tbody>${itemsHTML}</tbody></table>
  <div class="total-line">
    <p style="margin:5px 0"><strong>Sub-total:</strong> $${subtotal.toFixed(2)}</p>
    <p class="total-amount" style="margin:5px 0">Total: $${total.toFixed(2)}</p>
  </div>
  <div class="footer">
    <div>
      ${inv.payment_method ? `<p style="margin:0;font-size:13px"><strong>Payment method:</strong> ${esc(inv.payment_method)}</p>` : ''}
      ${inv.notes ? `<p style="margin:5px 0 0;font-size:12px;color:#666">${esc(inv.notes)}</p>` : ''}
    </div>
  </div>
  <p style="text-align:center;color:#999;margin-top:25px;font-size:14px;font-style:italic">Thank You!</p>
</body></html>`;
  };

  const handlePrint = (inv) => {
    const html = buildInvoiceHTML(inv);
    const win = window.open("", "_blank", "width=800,height=1100");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  const handleExportPDF = (inv) => {
    const html = buildInvoiceHTML(inv);
    const win = window.open("", "_blank", "width=800,height=1100");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    // Prompt save as PDF via print dialog
    setTimeout(() => win.print(), 500);
    toast.success("Usa 'Guardar como PDF' en el diálogo de impresión");
  };

  const filtered = invoices.filter(i =>
    i.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(i.invoice_number).includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-stone-50" data-testid="invoices-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="invoices-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Admin
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-600" /> Cotizaciones & Facturas
              </h1>
              <p className="text-xs text-stone-500">{invoices.length} facturas</p>
            </div>
          </div>
          <Button onClick={handleOpenCreate} className="bg-orange-600 hover:bg-orange-700" data-testid="create-invoice-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva Factura
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input placeholder="Buscar por cliente o número..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-white" />
        </div>

        {isLoading ? <div className="text-center py-12 text-stone-500">Cargando...</div> :
        filtered.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p className="text-stone-500 text-lg mb-2">No hay facturas</p>
            <Button onClick={handleOpenCreate} variant="outline"><Plus className="w-4 h-4 mr-2" /> Crear primera factura</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(inv => (
              <Card key={inv.id} className="hover:shadow-md transition-shadow" data-testid={`invoice-card-${inv.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-semibold">#{String(inv.invoice_number).padStart(4, '0')} - {inv.client_name}</p>
                      <p className="text-sm text-stone-500">{new Date(inv.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-bold text-stone-800">${(inv.total || 0).toFixed(2)}</span>
                    <Badge className={inv.status === 'sent' ? 'bg-blue-100 text-blue-700' : inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'}>
                      {inv.status === 'sent' ? 'Enviada' : inv.status === 'paid' ? 'Pagada' : 'Borrador'}
                    </Badge>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setPreviewInvoice(inv)} data-testid={`invoice-view-${inv.id}`}><Eye className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handlePrint(inv)} data-testid={`invoice-print-${inv.id}`}><Printer className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleExportPDF(inv)} title="Exportar PDF" data-testid={`invoice-pdf-${inv.id}`}><Download className="w-4 h-4 text-red-500" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleSendEmail(inv)} data-testid={`invoice-send-${inv.id}`}><Send className="w-4 h-4 text-blue-500" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(inv)}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(inv.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> {editingInvoice ? "Editar Factura" : "Nueva Factura"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select value={form.client_id} onValueChange={handleClientSelect}>
                  <SelectTrigger data-testid="invoice-client-select"><SelectValue placeholder="Seleccionar cliente..." /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Método de Pago</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm(p => ({ ...p, payment_method: v }))}>
                  <SelectTrigger data-testid="invoice-payment-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {["Zelle", "Cash", "Credit Card", "PayPal", "Transferencia", "Otro"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <Label className="mb-2 block">Items</Label>
              <div className="space-y-2">
                {products.length > 0 && (
                  <Select onValueChange={(productId) => {
                    const p = products.find(pr => pr.id === productId);
                    if (p) {
                      setForm(prev => ({
                        ...prev,
                        items: [...prev.items.filter(i => i.description), { description: p.name, qty: 1, price: p.price || 0, total: p.price || 0 }]
                      }));
                    }
                  }}>
                    <SelectTrigger className="border-dashed border-orange-300 text-orange-600" data-testid="invoice-product-select">
                      <SelectValue placeholder="Seleccionar del catálogo..." />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} {p.price ? `— $${p.price.toFixed(2)}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {form.items.map((item, idx) => (
                  <div key={item._uid || idx} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Input placeholder="Descripción" value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)} data-testid={`invoice-item-desc-${idx}`} />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" placeholder="Cant." value={item.qty} onChange={(e) => updateItem(idx, "qty", e.target.value)} min="1" />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" placeholder="Precio" value={item.price} onChange={(e) => updateItem(idx, "price", e.target.value)} step="0.01" min="0" />
                    </div>
                    <div className="col-span-2 text-right font-medium pt-2">${(parseFloat(item.total) || 0).toFixed(2)}</div>
                    <div className="col-span-1">
                      {form.items.length > 1 && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeItem(idx)}><Trash2 className="w-4 h-4" /></Button>}
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addItem} data-testid="invoice-add-item"><Plus className="w-4 h-4 mr-1" /> Agregar Item Manual</Button>
              </div>
            </div>

            <div className="text-right space-y-1 pt-2 border-t">
              <p className="text-lg font-bold">Total: <span className="text-orange-600">${calcSubtotal().toFixed(2)}</span></p>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notas adicionales..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-orange-600 hover:bg-orange-700" data-testid="invoice-save-btn">
              {isSaving ? "Guardando..." : (editingInvoice ? "Guardar" : "Crear Factura")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewInvoice} onOpenChange={(o) => !o && setPreviewInvoice(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Vista Previa</DialogTitle>
          </DialogHeader>
          {previewInvoice && (
            <div>
              <div id="invoice-print-area" data-testid="invoice-preview">
                {/* Invoice Template */}
                <div style={{ fontFamily: "Arial, sans-serif", maxWidth: 700, margin: "0 auto" }}>
                  {/* Header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, borderBottom: "3px solid #c45c2c", paddingBottom: 15 }}>
                    <div style={{ textAlign: "center" }}>
                      <img src="/assets/logo-sally-light.jpg" alt="SALLY Better" style={{ height: 80, width: "auto", borderRadius: 6, display: "block", margin: "0 auto" }} />
                      <a href="https://www.sallybetter.com" target="_blank" rel="noreferrer" style={{ display: "block", marginTop: 6, color: "#c45c2c", fontSize: 12, textDecoration: "none", textAlign: "center" }}>www.sallybetter.com</a>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <p style={{ margin: 0, fontWeight: "bold", fontSize: 14, color: "#c45c2c" }}>Invoice #{String(previewInvoice.invoice_number).padStart(4, '0')}</p>
                      <p style={{ margin: "3px 0 0", color: "#666", fontSize: 13 }}>Date: {new Date(previewInvoice.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {/* Billed to */}
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ margin: 0, color: "#999", fontSize: 11, textTransform: "uppercase" }}>Invoice for:</p>
                    <p style={{ margin: "3px 0 0", fontWeight: "bold", fontSize: 16 }}>{previewInvoice.client_name}</p>
                    {previewInvoice.client_email && <p style={{ margin: "2px 0 0", color: "#666", fontSize: 13 }}>{previewInvoice.client_email}</p>}
                  </div>

                  {/* Items Table */}
                  <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20 }}>
                    <thead>
                      <tr style={{ backgroundColor: "#c45c2c", color: "white" }}>
                        <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 13 }}>Description</th>
                        <th style={{ padding: "10px 12px", textAlign: "center", fontSize: 13, width: 70 }}>Qty.</th>
                        <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 13, width: 100 }}>Price</th>
                        <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 13, width: 100 }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(previewInvoice.items || []).map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                          <td style={{ padding: "10px 12px", fontSize: 14 }}>{item.description}</td>
                          <td style={{ padding: "10px 12px", textAlign: "center", fontSize: 14 }}>{item.qty}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 14 }}>${(parseFloat(item.price) || 0).toFixed(2)}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 14 }}>${(parseFloat(item.total) || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Totals */}
                  <div style={{ textAlign: "right", marginBottom: 20 }}>
                    <p style={{ margin: "5px 0", fontSize: 14 }}><strong>Sub-total:</strong> ${(previewInvoice.subtotal || 0).toFixed(2)}</p>
                    <p style={{ margin: "5px 0", fontSize: 22, color: "#c45c2c", fontWeight: "bold" }}>Total: ${(previewInvoice.total || 0).toFixed(2)}</p>
                  </div>

                  {/* Payment & Footer */}
                  <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #eee", paddingTop: 15, marginTop: 10 }}>
                    <div>
                      {previewInvoice.payment_method && <p style={{ margin: 0, fontSize: 13 }}><strong>Payment method:</strong> {previewInvoice.payment_method}</p>}
                      {previewInvoice.notes && <p style={{ margin: "5px 0 0", fontSize: 12, color: "#666" }}>{previewInvoice.notes}</p>}
                    </div>
                  </div>

                  <p style={{ textAlign: "center", color: "#999", marginTop: 25, fontSize: 14, fontStyle: "italic" }}>Thank You!</p>
                </div>
              </div>

              <div className="flex gap-2 mt-4 justify-end">
                <Button variant="outline" onClick={() => handleExportPDF(previewInvoice)} data-testid="invoice-preview-pdf">
                  <Download className="w-4 h-4 mr-2" /> Exportar PDF
                </Button>
                <Button variant="outline" onClick={() => handlePrint(previewInvoice)} data-testid="invoice-preview-print">
                  <Printer className="w-4 h-4 mr-2" /> Imprimir
                </Button>
                <Button variant="outline" onClick={() => handleSendEmail(previewInvoice)} data-testid="invoice-preview-send">
                  <Send className="w-4 h-4 mr-2" /> Enviar por Correo
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
